/**
 * @file contractUsage.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description How far a customer contract has got: ordered against agreed,
 *              its fulfilment badge, its fulfilment bucket, its state, whether
 *              it is in force today, and its margin. One rule for the contract
 *              screens (BlendPricing, StraightsPricing — through pricingHelpers)
 *              and the reps' contracts view (Cam, 28 Oct 2026 — O4), whose
 *              server copy is utils/contractUsage.js, generated by
 *              scripts/sync-shared-utils.js. Edit THIS file, then run it.
 *
 *   contractFulfilmentStatus / aggregateFulfilmentStatus moved here unchanged
 *   from pricingHelpers.js (which re-exports them), so the badge the office
 *   sees and the bucket a rep filters by read the same figures the same way.
 */

import { formatQuantity } from './numberFormatters';

/**
 * Compute the fulfilment status of a single blend/straight contract line.
 * Returns one of:
 *   'FULFILLED'   — qty - ordered < 1 (existing badge behaviour), OR caller
 *                   explicitly marked the row fulfilled via the manual
 *                   `isFulfilledOverride` flag.
 *   'UNFULFILLED' — period has ended AND >1 qty still remaining
 *   'UTILISE'     — period ends within 7 days AND >1 qty still remaining
 *   null          — none of the above (active, in window, with headroom)
 *
 * Inputs:
 *   { quantity, orderedQuantity, periodEnd, isFulfilledOverride }
 *     quantity / orderedQuantity may be strings (Postgres NUMERIC).
 *     periodEnd is an ISO date string ('YYYY-MM-DD') or Date or null.
 *     isFulfilledOverride: when truthy, force-return 'FULFILLED' without
 *       looking at qty/used. Set per-row via the contract / straights edit
 *       modal — lets the user close out a row that under-delivered or keep
 *       an over-delivered one visibly fulfilled.
 *
 * Pass a Date as `today` when testing; defaults to "right now" rounded down.
 */
export function contractFulfilmentStatus({ quantity, orderedQuantity, periodEnd, isFulfilledOverride }, today = null) {
  if (isFulfilledOverride) return 'FULFILLED';
  const total = parseFloat(quantity) || 0;
  const used = parseFloat(orderedQuantity) || 0;
  const remaining = total - used;
  if (total > 0 && remaining < 1) return 'FULFILLED';
  if (!periodEnd || remaining <= 1) return null;

  const end = periodEnd instanceof Date ? periodEnd : new Date(periodEnd);
  if (Number.isNaN(end.getTime())) return null;
  const ref = today instanceof Date ? today : new Date();
  // Use UTC midnight so locale-specific TZ skew doesn't flip days.
  const endDay = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate());
  const refDay = Date.UTC(ref.getUTCFullYear(), ref.getUTCMonth(), ref.getUTCDate());
  const daysLeft = Math.floor((endDay - refDay) / 86400000);
  if (daysLeft < 0) return 'UNFULFILLED';
  if (daysLeft <= 7) return 'UTILISE';
  return null;
}

/**
 * Roll up fulfilment status across an array of contract lines (used on a
 * group/expandable row where one badge has to represent the worst case).
 * Priority: UNFULFILLED > UTILISE > FULFILLED > null. FULFILLED only wins
 * when *every* member is fulfilled.
 */
export function aggregateFulfilmentStatus(members, today = null) {
  if (!Array.isArray(members) || members.length === 0) return null;
  const statuses = members.map(m =>
    contractFulfilmentStatus({
      quantity: m.quantity,
      orderedQuantity: m.ordered_quantity,
      periodEnd: m.period_end,
      isFulfilledOverride: m.is_fulfilled_override
    }, today)
  );
  if (statuses.includes('UNFULFILLED')) return 'UNFULFILLED';
  if (statuses.includes('UTILISE')) return 'UTILISE';
  if (statuses.every(s => s === 'FULFILLED')) return 'FULFILLED';
  return null;
}

// ─── The reps' contracts view (Cam, 28 Oct 2026) ────────────────────────────

/** The fulfilment buckets a rep filters by, in the order the screen offers them. */
export const FULFILMENT_BUCKETS = [
  { key: 'not_started', label: 'Not started' },
  { key: 'under_way', label: 'Under way' },
  { key: 'complete', label: 'Fulfilled' },
  { key: 'over', label: 'Over' },
  { key: 'no_quantity', label: 'No quantity set' }
];

export const FULFILMENT_KEYS = FULFILMENT_BUCKETS.map(b => b.key);

/** What a contract can be, drafts aside (a draft was never in place). */
export const CONTRACT_STATES = [
  { key: 'live', label: 'Live' },
  { key: 'on_hold', label: 'On hold' },
  { key: 'archived', label: 'Archived' }
];

export const CONTRACT_STATE_KEYS = CONTRACT_STATES.map(s => s.key);

// More ordered than agreed by more than rounding is "over"; the contract
// screens colour a negative remaining the same way.
const OVER_BY = 0.005;

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * The fulfilment bucket of a contract (or a straights month, or a group with
 * its shared quantity and its members' ordered summed):
 *   no_quantity  no quantity agreed (most compound contracts)
 *   over         more ordered than agreed — unless marked fulfilled by hand
 *   complete     marked fulfilled by hand, or less than 1 left (the badge's
 *                FULFILLED rule)
 *   not_started  nothing ordered yet
 *   under_way    the rest
 * A contract of under 1 agreed with nothing ordered is "not started", where
 * the badge (which never looks at what was ordered) calls it FULFILLED.
 */
export function fulfilmentBucket({ quantity, orderedQuantity, isFulfilledOverride = false }) {
  const agreed = num(quantity);
  const used = num(orderedQuantity);
  if (!(agreed > 0)) return 'no_quantity';
  if (used > agreed + OVER_BY) return isFulfilledOverride ? 'complete' : 'over';
  if (isFulfilledOverride) return 'complete';
  if (!(used > 0)) return 'not_started';
  if (agreed - used < 1) return 'complete';
  return 'under_way';
}

/** Ordered as a percentage of agreed, one decimal place; null with no quantity. */
export function fulfilmentPercent(quantity, orderedQuantity) {
  const agreed = num(quantity);
  if (!(agreed > 0)) return null;
  return Math.round((num(orderedQuantity) / agreed) * 1000) / 10;
}

/**
 * The words beside the bar: "54.00 of 220.00 T · 25%", "No quantity set ·
 * 27.00 T ordered", "… · marked fulfilled". `unit` is ' T' or '' (a contract
 * whose unit is not known shows bare figures — never a guessed T).
 */
export function fulfilmentText({ quantity, orderedQuantity, isFulfilledOverride = false, unit = '' }) {
  const agreed = num(quantity);
  const used = num(orderedQuantity);
  const u = unit ? ` ${String(unit).trim()}` : '';
  const marked = isFulfilledOverride ? ' · marked fulfilled' : '';
  if (!(agreed > 0)) return `No quantity set · ${formatQuantity(used)}${u} ordered${marked}`;
  const pct = fulfilmentPercent(agreed, used);
  return `${formatQuantity(used)} of ${formatQuantity(agreed)}${u} · ${Math.round(pct)}%${marked}`;
}

/** live / on_hold / archived — archived beats on hold. */
export function contractState({ isArchived = false, isOnHold = false } = {}) {
  if (isArchived) return 'archived';
  if (isOnHold) return 'on_hold';
  return 'live';
}

/** A group's state: live if any member is, else on hold if any is, else archived. */
export function groupState(states = []) {
  if (states.includes('live')) return 'live';
  if (states.includes('on_hold')) return 'on_hold';
  return states.length ? 'archived' : 'live';
}

/**
 * Is a contract's window (its own dates, else its period's) in force on
 * `today` ('YYYY-MM-DD')? 'past' | 'current' | 'future'; an open end never
 * ends, an open start has always begun.
 */
export function inForce(start, end, today) {
  const s = start ? String(start).slice(0, 10) : null;
  const e = end ? String(end).slice(0, 10) : null;
  if (e && e < today) return 'past';
  if (s && s > today) return 'future';
  return 'current';
}

/**
 * The margin a price makes on a cost: { amount, percent } — percent on COST,
 * as the straights view and the contract screens work it. Null without a
 * price, or without a cost (no cost is no margin, never 100%).
 */
export function marginOf(price, cost) {
  const p = num(price);
  const c = num(cost);
  if (!(p > 0) || !(c > 0)) return null;
  return {
    amount: Math.round((p - c) * 10000) / 10000,
    percent: Math.round(((p - c) / c) * 1000) / 10
  };
}

/** { min, max } of the positive figures, or null when there are none. */
export function rangeOf(values = []) {
  const v = values.map(num).filter(n => n > 0);
  if (!v.length) return null;
  return { min: Math.min(...v), max: Math.max(...v) };
}
